// PropertyDrilldown.jsx
import {
  AddHomeWorkOutlined,
  ApartmentOutlined,
  StoreOutlined,
} from "@mui/icons-material";
import { Box, Button, Card, Divider } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { isFunction, isObject } from "lodash";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDrilldownBase } from "../../../../pipelines/PipelineContext";
import { columnTypesBoolean } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesDollar } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import { columnTypesNumber } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import PinnedCommentsModal from "../../../../components/common/Loan/Comments/PinnedCommentsModal";
import PropertyAssociatedCommentsBadge from "../../../../components/common/Loan/PropertyAssociatedCommentsBadge";
import LoanDrilldownAddressSection from "../../../../components/common/LoanDrilldownAddressSection";
import TitledEditableFieldsRenderer from "../../../../components/common/TitledEditableFieldsRenderer";
import RffForm from "../../../../components/finalForm/RffForm";
import RffFormChanges from "../../../../components/finalForm/RffFormChanges";
import RffBooleanCheckbox from "../../../../components/finalForm/inputs/RffBooleanCheckbox";
import RffDateField from "../../../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../../../components/finalForm/inputs/RffDollarField";
import RffNumberField from "../../../../components/finalForm/inputs/RffNumberField";
import RffSelectAutocompleteField from "../../../../components/finalForm/inputs/RffSelectAutocompleteField";
import RffTextField from "../../../../components/finalForm/inputs/RffTextField";
import RffTextareaField from "../../../../components/finalForm/inputs/RffTextareaField";
import RffCheckboxGroup from "../../../../components/finalForm/shared/RffCheckboxGroup";
import RffConditional from "../../../../components/finalForm/shared/RffConditional";
import RffGroup from "../../../../components/finalForm/shared/RffGroup";
import Flx from "../../../../components/layout/Flx";
import SideCardModal from "../../../../components/modals/SideCardModal";
import TabPanel from "../../../../components/navigation/TabPanel";
import ToggleTabSwitcher from "../../../../components/navigation/ToggleTabSwitcher";
import Txt from "../../../../components/typography/Txt";
import TablePreviewCard from "../../../../components/ui/TablePreviewCard";
import TitledGroup from "../../../../components/ui/TitledGroup";
import { selectOptionsCommercialUse } from "../../../../constants/selectOptions/selectOptionsCommercialUse";
import { selectOptionsPropertyConditionAtOrigination } from "../../../../constants/selectOptions/selectOptionsPropertyConditionAtOrigination";
import { selectOptionsPropertyStatusAtOrigination } from "../../../../constants/selectOptions/selectOptionsPropertyStatusAtOrigination";
import { selectOptionsPropertyType } from "../../../../constants/selectOptions/selectOptionsPropertyType";
import { selectOptionsUnitLeaseTypes } from "../../../../constants/selectOptions/selectOptionsUnitLeaseTypes";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import RffBooleanSwitchField from "../../../../components/finalForm/inputs/RffBooleanSwitchField";
import RffSelectToggleField from "../../../../components/finalForm/inputs/RffSelectToggleField";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

function PropertyDrilldown({ selectedId }) {
  const { id: loanId } = useParams();
  const navigate = useNavigate();
  const drilldownBase = useDrilldownBase();

  const [selected, setSelected] = useState(null);
  const [associatedComments, setAssociatedComments] = useState(null);
  const [formSpy, setFormSpy] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [tab, setTab] = useState("Property Details");

  const onClose = useCallback(() => {
    navigate(`${drilldownBase}/${loanId}/properties`);
  }, [loanId, navigate, drilldownBase]);
  const memoizedSetTab = useCallback((newTab) => setTab(newTab), []);
  const subjectProperties = useMemo(
    () => loanDrilldown?.subjectProperties || [],
    [loanDrilldown],
  );

  const dispatch = useDispatch();
  const { updateSubjectProperty } = useUnderwritingHook();

  const onPropertyUpdate = ({ values, stopLoadingFn }) => {
    updateSubjectProperty({
      propertyId: selected?._id,
      loanId: loanDrilldown?._id,
      data: values,
      onSuccessFn: (response) => {
        // console.log("response", response);
        dispatch(loanDrilldownSet(response));
        stopLoadingFn();
      },
      onCompleteFn: () => {
        stopLoadingFn();
      },
    });
  };

  useEffect(() => {
    // if (isNil(selectedId)) return;
    if (isNil(selectedId)) {
      setSelected(null);
      return;
    }
    const property = subjectProperties?.find(
      (b) => b._id === selectedId || b.id === selectedId,
    );
    setSelected(property);
  }, [selectedId, subjectProperties]);
  // Title for the preview card
  const cardTitle = addressLabel(selected?.address) || "Property Details";

  /**
   * 2. Property Details
   */
  const propertyDetailsGroup = useMemo(() => {
    return [
      {
        name: "propertyType",
        label: "Property Type",
        type: "select",
        options: selectOptionsPropertyType,
      },
      {
        name: "proposedPropertyType",
        label: "Proposed Property Type",
        type: "select",
        options: selectOptionsPropertyType,
      },
      {
        name: "propertyRuralIndicator",
        label: "Property Rural Indicator",
        type: "booleanToggle",
      },
      {
        name: "stringAddressFinalTitle",
        label: "Property Address as shown on title",
      },
      {
        name: "floodZone",
        label: "Flood Zone",
        type: "booleanToggle",
      },
      {
        name: "floodCertDate",
        label: "Flood Cert Date",
        type: "date",
      },
      {
        name: "propertyListedForSale",
        label: "Property Listed For Sale",
        type: "booleanToggle",
      },
      {
        name: "lastListedForSaleDate",
        label: "Last Listed For Sale Date",
        type: "date",
      },
      {
        name: "lastListedForSalePrice",
        label: "Last Listed For Sale Price",
        type: "dollar",
      },
    ];
  }, []);

  /**
   * 3. Valuation Info
   */
  const valuationInfoGroup = useMemo(() => {
    return [
      {
        name: "asIsValue",
        label: "As-Is Value",
        type: "dollar",
      },
      {
        name: "secondaryAsIsValue",
        label: "Secondary As-Is Value",
        type: "dollar",
      },
      {
        name: "secondaryAsIsVariance",
        label: "Secondary As-Is Variance",
        type: "percent",
      },
      {
        name: "arv",
        label: "After Repair Value (ARV)",
        type: "dollar",
      },
      {
        name: "secondaryArv",
        label: "Secondary ARV",
        type: "dollar",
      },
      {
        name: "appraisalDate",
        label: "Appraisal Date",
        type: "date",
      },
      {
        name: "secondaryAppraisalDate",
        label: "Secondary Appraisal Date",
        type: "date",
      },
    ];
  }, []);

  /**
   * 4. Purchase Info
   */
  const purchaseInfoGroup = useMemo(() => {
    return [
      {
        name: "purchasePrice",
        label: "Purchase Price",
        type: "dollar",
      },
      {
        name: "purchaseDate",
        label: "Purchase Date",
        type: "date",
      },
      {
        name: "costBasis",
        label: "Cost Basis",
        type: "dollar",
      },
      {
        name: "additionalBasis",
        label: "Additional Basis",
        type: "dollar",
      },
      {
        name: "assignmentFees",
        label: "Assignment Fees",
        type: "dollar",
      },
      {
        name: "sellersConcession",
        label: "Seller's Concession",
        type: "dollar",
      },
    ];
  }, []);

  /**
   * 5. Construction / Rehab
   */
  const constructionInfoGroup = useMemo(() => {
    return [
      {
        name: "constructionBudget",
        label: "Construction Budget",
        type: "dollar",
      },
      {
        name: "interestReserves",
        label: "Interest Reserves",
        type: "dollar",
      },
      {
        name: "financedBudget",
        label: "Calculated $ Financed Budget",
        // label: "Financed Budget",
        type: "dollar",
      },
      {
        name: "financedBudgetPercent",
        label: "Percent Financed Budget",
        // label: "Calculated Financed Budget",
        // label: "Financed Budget Percent",

        type: "percent",
      },
      {
        name: "budgetIsFinal",
        label: "Budget Is Final",
        type: "booleanToggle",
      },
      {
        name: "budgetMatchesAppraisal",
        label: "Budget Matches Appraisal",
        type: "booleanToggle",
      },
      {
        name: "finalBudgetUploaded",
        label: "Final Budget Uploaded",
        type: "booleanToggle",
      },
      {
        name: "finalBudgetFeasible",
        label: "Final Budget Feasible",
        type: "booleanToggle",
      },
      // {
      //   name: "scopeOfWork",
      //   label: "Scope Of Work",
      //   type: "number",
      // },
    ];
  }, []);

  /**
   * 6. Property Condition
   */
  const propertyConditionGroup = useMemo(() => {
    return [
      {
        name: "propertyConditionAtOrigination",
        label: "Condition at Origination",
        type: "select",
        options: selectOptionsPropertyConditionAtOrigination,
      },
      {
        name: "propertyStatusAtOrigination",
        label: "Status at Origination",
        type: "selectMultiple",
        // type: "select",
        options: selectOptionsPropertyStatusAtOrigination,
      },
    ];
  }, []);

  /**
   * 7. Property Notes
   */

  /**
   * 8. Units / Square Footage
   */

  /**
   * 9. Expenses (Annual & Monthly)
   */
  const expensesGroup = useMemo(() => {
    return [
      // Annual
      {
        name: "annualTaxes",
        label: "Annual Taxes",
        type: "dollar",
      },
      {
        name: "annualInsurance",
        label: "Annual Insurance",
        type: "dollar",
      },
      {
        name: "annualFloodInsurance",
        label: "Annual Flood Insurance",
        type: "dollar",
      },
      {
        name: "annualHoaFees",
        label: "Annual HOA Fees",
        type: "dollar",
      },
      {
        name: "annualPropertyMgmtFees",
        label: "Annual Property Mgmt Fees",
        type: "dollar",
      },
      {
        name: "annualOtherExpense",
        label: "Annual Other Expense",
        type: "dollar",
      },
      // Monthly
      {
        name: "monthlyTaxes",
        label: "Monthly Taxes",
        type: "dollar",
      },
      {
        name: "monthlyInsurance",
        label: "Monthly Insurance",
        type: "dollar",
      },
      {
        name: "monthlyFloodInsurance",
        label: "Monthly Flood Insurance",
        type: "dollar",
      },
      {
        name: "monthlyHoaFees",
        label: "Monthly HOA Fees",
        type: "dollar",
      },
      {
        name: "monthlyPropertyMgmtFees",
        label: "Monthly Property Mgmt Fees",
        type: "dollar",
      },
      {
        name: "monthlyOtherExpense",
        label: "Monthly Other Expense",
        type: "dollar",
      },
    ];
  }, []);

  // If no property selected, don't render anything
  if (isNil(selected) || isEmpty(selected)) {
    return null;
  }
  return (
    <>
      <TablePreviewCard
        title={cardTitle}
        onClose={onClose}
        formSpy={formSpy}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        titleEndContent={
          <PropertyAssociatedCommentsBadge
            propertyId={selectedId}
            onClick={(c) => {
              setAssociatedComments(c);
            }}
          />
        }
        setFormSpy={setFormSpy}
        searchPlaceholder="Search in property..."
        icon={<StoreOutlined className="thin" />}
      >
        <Flx gap={4} wrap sx={{ flexGrow: 1 }}>
          <Flx
            column
            gap={1.5}
            sx={{ flexGrow: 1, flexBasis: "680px", flexShrink: 1 }}
          >
            <LoanDrilldownAddressSection
              quickFilter={quickFilter}
              property={selected}
            />

            <ToggleTabSwitcher
              value={tab}
              variant={"underline"}
              onChange={memoizedSetTab}
              tabs={[
                "Property Details",
                "Valuation Info",
                "Purchase Info",
                "Construction/Rehab",
                "Units/Square Footage",
                "Property Condition",
                "Expenses",
                "All",
              ]}
            />
            <Flx column gap={3}>
              <PropertyTab tab="Property Details" tabValue={tab}>
                <TitledEditableFieldsRenderer
                  uppercase
                  title="Property Details"
                  quickFilter={quickFilter}
                  onUpdateFn={onPropertyUpdate}
                  data={selected}
                  fields={propertyDetailsGroup}
                />
              </PropertyTab>

              <PropertyTab tab="Valuation Info" tabValue={tab}>
                <TitledEditableFieldsRenderer
                  uppercase
                  title="Valuation Info"
                  quickFilter={quickFilter}
                  onUpdateFn={onPropertyUpdate}
                  data={selected}
                  fields={valuationInfoGroup}
                />
              </PropertyTab>
              <PropertyTab tab="Purchase Info" tabValue={tab}>
                <TitledEditableFieldsRenderer
                  uppercase
                  title="Purchase Info"
                  quickFilter={quickFilter}
                  onUpdateFn={onPropertyUpdate}
                  data={selected}
                  fields={purchaseInfoGroup}
                />
              </PropertyTab>
              <PropertyTab tab="Construction/Rehab" tabValue={tab}>
                <TitledEditableFieldsRenderer
                  uppercase
                  title="Construction/Rehab"
                  quickFilter={quickFilter}
                  onUpdateFn={onPropertyUpdate}
                  data={selected}
                  fields={constructionInfoGroup}
                />
              </PropertyTab>
              <UnitTabContent
                tab={tab}
                quickFilter={quickFilter}
                onPropertyUpdate={onPropertyUpdate}
                loanId={loanId}
                selected={selected}
              />
              <PropertyTab tab="Property Condition" tabValue={tab}>
                <TitledEditableFieldsRenderer
                  uppercase
                  title="Property Condition"
                  size={12}
                  quickFilter={quickFilter}
                  onUpdateFn={onPropertyUpdate}
                  data={selected}
                  fields={propertyConditionGroup}
                />
              </PropertyTab>
              <PropertyTab tab="Expenses" tabValue={tab}>
                <TitledEditableFieldsRenderer
                  uppercase
                  title="Expenses"
                  size={12}
                  quickFilter={quickFilter}
                  onUpdateFn={onPropertyUpdate}
                  data={selected}
                  fields={expensesGroup}
                />
              </PropertyTab>
            </Flx>
          </Flx>
          {/* {isNil(selectedId) ? null : (
            <>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flexGrow: 1, flexBasis: "220px", flexShrink: 1 }}>
                <Flx column gap={4}>
                  <DetailSection
                    title="Property Associations"
                    // variant="card"
                    collapsible={true}
                    defaultExpanded={true}
                    icon={
                      <HubOutlined className="thin" fontSize="small" />
                      // <AttachFileOutlined className="thin" fontSize="small" />
                    }
                  >
                    <AssociationsSelector
                      associationTypeName="subjectProperties"
                      associationTypeId={selectedId}
                      associations={selected?.associations}
                    />
                  </DetailSection>
               
                </Flx>
              </Box>
            </>
          )} */}
        </Flx>
      </TablePreviewCard>
      {!isNil(associatedComments) && (
        <PinnedCommentsModal
          title="Property Comments"
          show={associatedComments}
          fixed
          comments={associatedComments}
          loan={loanDrilldown}
          // fileId={fileId}
          subjectPropertyIds={[selectedId]}
          onClose={() => setAssociatedComments(null)}
        />
      )}
    </>
  );
}

const UnitTabContent = ({
  tab,
  quickFilter,
  onPropertyUpdate,
  loanId,
  selected,
}) => {
  console.log("PROPERTY", selected);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const unitsGroup = useMemo(() => {
    return [
      {
        name: "unitCount",
        label: "Unit Count",
      },
      {
        name: "vacantUnitCount",
        label: "Vacant Unit Count",
      },
      {
        name: "residentialUnitCount",
        label: "Residential Unit Count",
      },
      {
        name: "commercialUnitCount",
        label: "Commercial Unit Count",
      },
      {
        name: "residentialSqFootage",
        label: "Residential Sq. Footage",
        type: "float",
      },
      {
        name: "commercialSqFootage",
        label: "Commercial Sq. Footage",
        type: "float",
      },
      {
        name: "afterRenoSqFootage",
        label: "After Reno Sq. Footage",
        type: "float",
      },
    ];
  }, []);

  const propertyUnitsArray = useMemo(() => {
    if (!isArray(selected?.units)) return [];

    // return [
    //   {
    //     unitSquareFootage: 1200,
    //     leaseType: "12-Month Lease - Occupied",
    //     leaseStartDate: "2025-09-01T04:00:00.000Z",
    //     leaseEndDate: "2026-09-01T04:00:00.000Z",
    //     unitMonthlyMarketRent: 3000,
    //     unitMonthlyInPlaceRent: 2600,
    //     unitVacant: false,
    //     unitNotes: "This is a good place to live",
    //     unitNumber: "101",
    //     residentialUnit: true,
    //   },
    // ];
    return selected?.units;
  }, [selected]);

  const handleOnModalClose = useCallback((props) => {
    setShow(false);
    setEditing(false);
  }, []);

  return (
    <PropertyTab tab="Units/Square Footage" tabValue={tab}>
      <TitledEditableFieldsRenderer
        uppercase
        title="Units/Square Footage"
        quickFilter={quickFilter}
        onUpdateFn={onPropertyUpdate}
        data={selected}
        bodySx={{ pt: 1 }}
        fields={unitsGroup}
      />
      <TitledGroup
        title={"Property Unit Details"}
        uppercase
        // bodySx={{ p: 0 }}
        bodySx={{ p: 1 }}
        headerEndContent={
          <Flx gap={1} ac>
            {/* {isEmpty(propertyUnitsArray) ? null : editing ? (
              <Button
                size="small"
                color={"error"}
                variant="outlined"
                onClick={() => setEditing(null)}
              >
                Cancel Editing
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlined className="thin" />}
                onClick={() => setEditing(true)}
              >
                Edit Units
              </Button>
            )} */}
            <Button
              size="small"
              startIcon={<AddHomeWorkOutlined className="thin" />}
              onClick={() => setShow(true)}
            >
              Add Unit
            </Button>
          </Flx>
        }
      >
        <PropertyUnitsTable
          units={propertyUnitsArray}
          editing={editing}
          loanId={loanDrilldown?._id}
          propertyId={selected?._id}
          property={selected}
        />
        <PropertyUnitCreateModal
          show={show}
          onClose={handleOnModalClose}
          loanId={loanDrilldown?._id}
          propertyId={selected?._id}
          onSuccessFn={handleOnModalClose}
        />
      </TitledGroup>
    </PropertyTab>
  );
};

export const PropertyUnitsTable = ({
  units,
  autoHeight = true,
  quickFilter,
  height = "100%",
  editing,
  loanId,
  propertyId,
  property,
}) => {
  const gridRef = useRef(null);
  const [editingData, setEditingData] = useState(null);
  const columnDefs = useMemo(() => {
    return [
      {
        field: "unitNumber",
        headerName: "Unit #",
        width: 100,
      },
      {
        field: "unitSquareFootage",
        headerName: "Square Ft.",
        width: 100,
        filter: false,
        type: "number",
      },
      {
        field: "leaseType",
        headerName: "Lease Type",
      },
      {
        field: "leaseStartDate",
        headerName: "Lease Start",
        width: 100,
        filter: false,
        type: "date",
      },
      {
        field: "leaseEndDate",
        headerName: "Lease End",
        width: 100,
        type: "date",
        filter: false,
      },
      {
        field: "unitMonthlyMarketRent",
        headerName: "Market Rent",
        width: 110,
        type: "dollar",
        filter: false,
      },
      {
        field: "unitMonthlyInPlaceRent",
        headerName: "In-Place Rent",
        width: 110,
        filter: false,
        type: "dollar",
      },
      {
        field: "unitVacant",
        headerName: "Is Vacant",
        width: 90,
        filter: false,
        type: "boolean",
      },
      {
        field: "residentialUnit",
        headerName: "Is Residential",
        width: 110,
        filter: false,
        type: "boolean",
      },
      {
        field: "commercialUnit",
        headerName: "Is Commercial",
        width: 115,
        filter: false,
        type: "boolean",
      },
      {
        field: "commercialUse",
        headerName: "Commercial Use",
        filter: false,
        // width: 100,
      },
      {
        field: "unitNotes",
        headerName: "Notes",
        flex: 1,
        minWidth: 200,
      },
    ];
  }, [editing]);

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesNumber(),
      ...columnTypesDollar(),
      ...columnTypesBoolean(),
    };
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      autoHeight: true,
      cellClass: "ag-cell-vertical-center",
      // flex: 1,
      // cellStyle: {
      //   display: "flex",
      //   alignItems: "center",
      //   justifyContent: "center", // Optional: also center horizontally
      // },
    }),
    [],
  );

  const handleRowSelectionChange = useCallback((params) => {
    const selected = params.api.getSelectedRows();

    if (isArray(selected)) {
      setEditingData(selected[0]);
    } else {
      setEditingData(null);
    }
  }, []);

  const handleOnModalClose = useCallback((props) => {
    if (gridRef.current.api) {
      // console.log("gridRef", gridRef);
      gridRef.current.api.deselectAll();
    }
    setEditingData(null);
  }, []);

  const selectionColumnDef = useMemo(() => {
    return {
      sortable: true,
      resizable: true,
      width: 44,
      suppressHeaderMenuButton: false,
      headerTooltip: "Checkboxes indicate selection",
    };
  }, []);

  if (isEmpty(units)) {
    return (
      <Txt secondary italic sx={{ p: 1, lineHeight: 2 }}>
        No property units added yet.
      </Txt>
    );
  }

  return (
    <Box sx={{ m: -2, mb: -4, mt: 0 }}>
      <PropertyUnitUpdateModal
        show={editingData}
        propertyUnitData={editingData}
        onClose={handleOnModalClose}
        loanId={loanId}
        propertyId={propertyId}
        property={property}
        onSuccessFn={handleOnModalClose}
      />
      <AgGridTableWrapper
        suppressMinHeightWhenPopulated={units}
        height={height}
        simpleTable
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,

          overflow: "hidden",
        }}
        size={4}
      >
        <AgGridReact
          ref={gridRef}
          rowData={units}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          defaultColDef={defaultColDef}
          selectionColumnDef={selectionColumnDef}
          onSelectionChanged={handleRowSelectionChange}
          // loading={isEmpty(units)}
          quickFilterText={quickFilter}
          domLayout={autoHeight ? "autoHeight" : "normal"}
          // detailRowHeight={310}

          // rowSelection={editing ? "multiple" : "single"}
          rowSelection={{
            mode: "singleRow",
          }}
          // rowSelection={
          //   !editing
          //     ? {}
          //     : {
          //         mode: "singleRow",
          //       }
          // }
          // detailCellRenderer={detailCellRenderer}
        />
      </AgGridTableWrapper>
    </Box>
  );
};

export const PropertyUnitCreateModal = ({
  show,
  onClose,
  loanId,
  propertyId,
  onSuccessFn,
}) => {
  const { createSubjectPropertyUnit, loading } = useUnderwritingHook();
  const dispatch = useDispatch();

  const onSubmit = useCallback(
    (values) => {
      createSubjectPropertyUnit({
        loanId: loanId,
        propertyId: propertyId,
        propertyData: values,
        onSuccessFn: (response) => {
          dispatch(loanDrilldownSet(response));
          if (isFunction(onSuccessFn)) {
            onSuccessFn();
          }
        },
      });
    },
    [createSubjectPropertyUnit, loanId, propertyId, dispatch],
  );

  const handleOnClose = useCallback(() => {
    onClose(false);
  }, []);

  return (
    <SideCardModal
      title="Create Property Unit"
      show={show}
      icon={<ApartmentOutlined className="thin" />}
      onClose={handleOnClose}
      bodySx={{ p: 3 }}
    >
      <RffForm
        submitText={"Create Unit"}
        onSubmit={onSubmit}
        loading={loading}
        // initialValues={{
        //   unitSquareFootage: 1200,
        //   leaseType: "12-Month Lease - Occupied",
        //   leaseStartDate: "2025-09-01T04:00:00.000Z",
        //   leaseEndDate: "2026-09-01T04:00:00.000Z",
        //   unitMonthlyMarketRent: 3000,
        //   unitMonthlyInPlaceRent: 2600,
        //   unitVacant: false,
        //   unitNotes: "This is a good place to live",
        //   unitNumber: "101",
        //   residentialUnit: true,
        // }}
        //
      >
        <SubjectPropertyUnitFormQuestions />
      </RffForm>
    </SideCardModal>
  );
};

const PropertyUnitUpdateModal = ({
  onClose,
  loanId,
  propertyId,
  propertyUnitData,
  property,
  onSuccessFn,
}) => {
  const { updateSubjectPropertyUnit, loading } = useUnderwritingHook();
  const dispatch = useDispatch();

  const handleOnClose = useCallback(() => {
    onClose(false);
  }, []);

  const onSubmit = (changedValues, { form, values, changes }) => {
    // console.log("File changes submitted", {
    //   changedValues,
    //   form,
    //   values,
    //   changes,
    // });

    updateSubjectPropertyUnit({
      loanId: loanId,
      propertyId: propertyId,
      unitData: { ...changedValues },
      unitId: propertyUnitData?._id,
      onSuccessFn: (response) => {
        dispatch(loanDrilldownSet(response));
        if (isFunction(onSuccessFn)) {
          onSuccessFn();
        }
      },
    });
  };

  const showModal = useMemo(() => {
    if (isObject(propertyUnitData) && !isEmpty(propertyUnitData)) {
      return true;
    }
    return false;
  }, [propertyUnitData]);

  return (
    <SideCardModal
      title="Update Property Unit"
      description={
        addressLabel(property?.address)
      }
      show={showModal}
      icon={<ApartmentOutlined className="thin" />}
      onClose={handleOnClose}
      bodySx={{ p: 3 }}
    >
      <RffFormChanges
        submitText="Update Unit"
        onSubmit={onSubmit}
        initialValues={propertyUnitData}
        loading={loading}
      >
        <SubjectPropertyUnitFormQuestions
          onSuccessFn={onClose}
          initialValues={propertyUnitData}
          onSubmit={onSubmit}
          loading={loading}
        />
      </RffFormChanges>
    </SideCardModal>
  );
};

export const SubjectPropertyUnitFormQuestions = ({ children }) => {
  return (
    <Flx column gap={3}>
      <RffGroup
        suppressBottomMargin
        disableGrid
        isGridChild
        label="Unit Details"
      >
        <Card sx={{ mt: 0.5, boxShadow: "none" }} variant="elevated">
          <RffGroup suppressBottomMargin isGridChild>
            <RffTextField name={`unitNumber`} label="Unit Number" size={3} />
            <RffNumberField
              name={`unitSquareFootage`}
              label="Unit Square Footage"
              size={3}
            />
            <RffSelectAutocompleteField
              name={`leaseType`}
              size={6}
              label="Lease Type"
              options={selectOptionsUnitLeaseTypes}
            />
            <RffDateField
              name={`leaseStartDate`}
              label="Lease Start Date"
              size={6}
            />
            <RffDateField
              name={`leaseEndDate`}
              label="Lease End Date"
              size={6}
            />
            <RffDollarField
              name={`unitMonthlyInPlaceRent`}
              label="Monthly In-Place Rent"
              size={6}
            />
            <RffDollarField
              name={`unitMonthlyMarketRent`}
              size={6}
              label="Monthly Market Rent"
            />

            <RffTextareaField
              name={`unitNotes`}
              label="Unit Notes"
              minHeight={"80px"}
            />
          </RffGroup>
        </Card>
      </RffGroup>
      <RffGroup disableGrid isGridChild label="Unit Status & Usage">
        <Card sx={{ mt: 0.5, boxShadow: "none" }} variant="elevated">
          <Flx column gap={2}>
            <RffBooleanSwitchField
              suppressGrid
              name={`unitVacant`}
              label="Currently Vacant"
              description={"Enable if the unit is currently vacant"}
            />
            <Divider />
            <RffBooleanSwitchField
              suppressGrid
              name={`residentialUnit`}
              label="Residential Use"
              description={"Enable if the unit is residential use"}
            />
            <Divider />
            <RffBooleanSwitchField
              suppressGrid
              name={`commercialUnit`}
              label="Commercial Use"
              description={"Enable if the unit is commercial use"}
            />

            <RffConditional field={"commercialUnit"} value={true}>
              <>
                <Divider />
                <RffSelectToggleField
                  suppressGrid
                  name={`commercialUse`}
                  label="Commercial Use Type"
                  options={selectOptionsCommercialUse}
                />
              </>
            </RffConditional>
          </Flx>
        </Card>
      </RffGroup>

      {children}
    </Flx>
  );
  return (
    <RffGroup>
      <RffTextField name={`unitNumber`} label="Unit Number" size={3} />
      <RffNumberField
        name={`unitSquareFootage`}
        label="Unit Square Footage"
        size={3}
      />
      <RffSelectAutocompleteField
        name={`leaseType`}
        size={6}
        label="Lease Type"
        options={selectOptionsUnitLeaseTypes}
      />
      <RffDateField name={`leaseStartDate`} label="Lease Start Date" size={6} />
      <RffDateField name={`leaseEndDate`} label="Lease End Date" size={6} />
      <RffDollarField
        name={`unitMonthlyInPlaceRent`}
        label="Monthly In-Place Rent"
        size={6}
      />
      <RffDollarField
        name={`unitMonthlyMarketRent`}
        size={6}
        label="Monthly Market Rent"
      />
      <RffGroup
        suppressBottomMargin
        disableGrid
        isGridChild
        label="Unit Status & Usage"
      >
        <Card sx={{ mt: 0.5, boxShadow: "none" }} variant="elevated">
          <Flx column gap={2}>
            <RffBooleanSwitchField
              suppressGrid
              name={`unitVacant`}
              label="Currently Vacant"
              description={"Enable if the unit is currently vacant"}
            />
            <Divider />
            <RffBooleanSwitchField
              suppressGrid
              name={`residentialUnit`}
              label="Residential Use"
              description={"Enable if the unit is residential use"}
            />
            <Divider />
            <RffBooleanSwitchField
              suppressGrid
              name={`commercialUnit`}
              label="Commercial Use"
              description={"Enable if the unit is commercial use"}
            />

            <RffConditional field={"commercialUnit"} value={true}>
              <>
                <Divider />
                <RffSelectToggleField
                  suppressGrid
                  name={`commercialUse`}
                  label="Commercial Use Type"
                  options={selectOptionsCommercialUse}
                />
              </>
            </RffConditional>
          </Flx>
        </Card>
      </RffGroup>

      <RffTextareaField
        name={`unitNotes`}
        label="Unit Notes"
        minHeight={"80px"}
      />
      {children}
    </RffGroup>
  );
};
const PropertyTab = ({ tab, tabValue, children }) => {
  return (
    <TabPanel value={tab} tabValue={tabValue} forceDisplay="All">
      {children}
    </TabPanel>
  );
};

export default React.memo(PropertyDrilldown);
